// ════════════════════════════════════════════════════════════
//  BattleCamera.jsx — the anime broadcast camera
// ════════════════════════════════════════════════════════════
//  It frames the duel the way the show would: low, three-quarter,
//  behind your being and swung off to one side so both fighters and
//  the space between them read. It breathes in and out with their
//  distance, punches in on a clash, shakes on the big hits, and
//  takes over completely for the cinematics — a U'hlti's tell, an
//  Ascent, a ruin.
//
//  THE CAMERA CONTRACT (ie-3d-conversion): drag to orbit, wheel to
//  zoom, and the pose you leave it in is kept — it never snaps
//  back. `R` (or the HUD's ⟲) is Default, the only reset.
//
//  THE FRONT ROW (caP'n 2026-10-17): when a spectator sits down (userCam.view ==="front",
//  a seat in userCam.seat — see frontRow.js) the camera leaves the broadcast and sits in
//  that seat in the floating stands: it tracks the fighters (a binocular view that frames
//  them) until you drag to look around by hand, then it stays where you looked
//  (the CAMERA CONTRACT) until R / "Eyes on the fight" puts it back on them.
//
//  THE SKY BOX (caP'n 2026-10-17): userCam.view === "sky" puts you IN the box (three/SkyBox.jsx).
//  Standing, the camera follows your body from behind — drag (any button) to swing round it,
//  wheel to come closer, and it stays where you left it (the CAMERA CONTRACT); R puts it back
//  behind you facing the fight. Seated, the camera is your eyes: the Front Row's binocular
//  view from your seat (frontFrame, with the seat's eye passed in).
//
//  Everything is smoothed frame-rate independently, and nothing in
//  here touches React.
// ════════════════════════════════════════════════════════════
import { useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { seatWorld, FRONT } from "./frontRow.js";
import { SKY, SKY_ANGLE, skyBob, skyToWorld, seatSpot, SIT, bodyH } from "../data/skybox.js";
import { BOX } from "../online/skybox.js";

const PITCH = 0.36;             // radians above the horizon
const SIDE_SWING = 0.62;        // how far off "straight behind you" it sits
const DIST_MIN = 7.4, DIST_MAX = 15;
const TARGET_Y = 0.95;

const v = new THREE.Vector3(), tgt = new THREE.Vector3();
const damp = (cur, want, rate, dt) => cur + (want - cur) * (1 - Math.exp(-rate * dt));
const dampAngle = (cur, want, rate, dt) => {
  let d = want - cur;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return cur + d * (1 - Math.exp(-rate * dt));
};

// ── THE FRONT ROW frame: one seat in the stands, eyes on the fight (or wherever you looked) ──
function frontFrame(state, dt, b, uc, s, camera, cue, eye = null) {
  const f = s.f || (s.f = { yaw: null, pitch: 0, fov: null, tx: 0, ty: 1.1, tz: 0 });
  const seat = eye || seatWorld(uc.seat, state.clock.elapsedTime);   // `eye`: a Sky Box seat's eye, already at eye height
  const px = seat.x, py = seat.y + (eye ? 0 : FRONT.eye), pz = seat.z;
  const act = (side) => { const sd = b.sides[side]; return sd.active >= 0 ? sd.roster[sd.active] : null; };
  const A = act(0), B = act(1);
  // what the eyes want: the cinematic's star, else both fighters, else the middle of the plate
  let tx = 0, ty = 1.1, tz = 0, spread = 6;
  const cine = b.cinematic;
  let star = null;
  if (cine) for (const sd of b.sides) for (const r of sd.roster) if (r.uid === cine.uid) star = r;
  if (cine && star) { tx = star.x; tz = star.z; ty = 1.0; spread = 5; }
  else if (A || B) {
    const a = A || B, c = B || A;
    tx = (a.x + c.x) / 2; tz = (a.z + c.z) / 2; spread = Math.hypot(c.x - a.x, c.z - a.z) + FRONT.pad;
  }
  f.tx = damp(f.tx, tx, 3.2, dt); f.ty = damp(f.ty, ty, 3.2, dt); f.tz = damp(f.tz, tz, 3.2, dt);
  const dx = f.tx - px, dz = f.tz - pz, dy = f.ty - py, hd = Math.max(1, Math.hypot(dx, dz));
  const trackYaw = Math.atan2(dx, dz), trackPitch = Math.atan2(dy, hd);
  const free = !!uc.free;
  const wantYaw = free ? uc.lookYaw : trackYaw, wantPitch = free ? uc.lookPitch : trackPitch;
  const rate = free ? FRONT.lookRate : FRONT.trackRate;
  if (f.yaw == null) { f.yaw = wantYaw; f.pitch = wantPitch; }
  f.yaw = dampAngle(f.yaw, wantYaw, rate, dt);
  f.pitch = damp(f.pitch, wantPitch, rate, dt);
  // field of view: binoculars that frame the fight, or an eye's worth when looking around; the wheel scales either
  const zoom = uc.seatZoom || 1;
  const trackFov = THREE.MathUtils.clamp(2 * Math.atan(spread / 2 / hd) * 57.2958, FRONT.fovMin, FRONT.fovMax);
  const wantFov = THREE.MathUtils.clamp((free ? FRONT.fovFree : trackFov) * zoom, 5, 75);
  if (f.fov == null) f.fov = wantFov;
  f.fov = damp(f.fov, wantFov, 3, dt);
  // the punch hook still lands (a little) and breathes back; the shake is drained so it can never pile up
  s.fov = damp(s.fov, 48, 3.5, dt);
  let sx = 0, sy = 0;
  for (const k of s.shake) {
    k.t -= dt;
    const a = k.amp * Math.max(0, k.t / k.dur) * 0.35;
    sx += Math.sin(s.t * 71 + k.dur * 13) * a; sy += Math.sin(s.t * 53 + k.amp * 7) * a;
  }
  for (let i = s.shake.length - 1; i >= 0; i--) if (s.shake[i].t <= 0) { s.shake[i] = s.shake[s.shake.length - 1]; s.shake.pop(); }
  const cp = Math.cos(f.pitch);
  camera.position.set(px, py, pz);
  camera.lookAt(px + Math.sin(f.yaw) * cp * 10 + sx * 0.04, py + Math.sin(f.pitch) * 10 + sy * 0.04, pz + Math.cos(f.yaw) * cp * 10);
  const fovNow = f.fov + (s.fov - 48) * 0.5;
  if (Math.abs(camera.fov - fovNow) > 0.01) { camera.fov = fovNow; camera.updateProjectionMatrix(); }
  // published for the controls (drag starts from where the head already is) and for the crowd (hide the fan in your seat)
  uc.frontYaw = f.yaw; uc.frontPitch = f.pitch; uc.camYaw = f.yaw; uc.camFov = camera.fov;
  const c = cue && cue.current;
  if (c) { if (eye) c.hideOn = 0; else { c.hideOn = 1; c.hideX = seat.x; c.hideZ = seat.z; } }
}

// ── THE SKY BOX: seated → your eyes (frontFrame); standing → behind you ──
export const skyCamDefault = (uc) => { uc.skyYaw = SKY_ANGLE; uc.skyPitch = SKY.cam.pitch; uc.skyDist = SKY.cam.dist; uc.free = false; uc.seatZoom = 1; };
function skyFrame(state, dt, b, uc, s, camera, cue) {
  const t = state.clock.elapsedTime, m = BOX.me, h = bodyH(m.body), floor = SKY.y + skyBob(t);
  if (uc.skyYaw == null) skyCamDefault(uc);
  if (s.skySeat !== m.seat) { s.skySeat = m.seat; uc.free = false; uc.seatZoom = 1; }   // sitting / standing always starts eyes-on-the-fight
  if (m.seat >= 0) {
    const sp = seatSpot(m.seat), w = skyToWorld(sp.x, sp.z + SIT.back + 0.08);
    s.k = null;   // standing camera starts fresh when you get up
    frontFrame(state, dt, b, uc, s, camera, cue, { x: w.x, y: floor + SKY.seatH + 0.04 + SKY.seatedEye * h, z: w.z });
    return;
  }
  s.f = null;     // and the seated eyes start fresh when you sit back down
  const w = skyToWorld(m.x, m.z), ty = floor + SKY.cam.lift * (h / 1.5);
  const k = s.k || (s.k = { x: w.x, y: ty, z: w.z, yaw: uc.skyYaw, pitch: uc.skyPitch, dist: uc.skyDist });
  k.x = damp(k.x, w.x, 10, dt); k.y = damp(k.y, ty, 10, dt); k.z = damp(k.z, w.z, 10, dt);
  k.yaw = dampAngle(k.yaw, uc.skyYaw, 14, dt); k.pitch = damp(k.pitch, uc.skyPitch, 14, dt); k.dist = damp(k.dist, uc.skyDist, 8, dt);
  const cp = Math.cos(k.pitch);
  camera.position.set(k.x + Math.sin(k.yaw) * k.dist * cp, k.y + Math.sin(k.pitch) * k.dist, k.z + Math.cos(k.yaw) * k.dist * cp);
  camera.lookAt(k.x, k.y, k.z);
  s.fov = damp(s.fov, 48, 3.5, dt);
  const fovNow = 52 + (s.fov - 48) * 0.5;
  if (Math.abs(camera.fov - fovNow) > 0.01) { camera.fov = fovNow; camera.updateProjectionMatrix(); }
  s.shake.length = 0;   // the fight's shake belongs to the broadcast, not your stroll
  uc.camYaw = k.yaw;
  const c = cue && cue.current; if (c) c.hideOn = 0;
}

// drag to orbit, wheel to zoom — the pose is kept. One binder for the fight's camera AND the
// Sky Box at ringside (caP'n 2026-10-20: "When I go ringside I should be able to go immediately into the sky box").
function bindCameraInput(gl, userCam) {
  const el = gl.domElement;
  let drag = null;
  // RIGHT-drag orbits (left click is the Lyt Attack, the mouse is the aim)
  // In the FRONT ROW any button drags your head round (a spectator has no other use for the left one).
  const front = () => (userCam.current.view === "front" && !!userCam.current.seat) || (userCam.current.view === "sky" && BOX.me.seat >= 0);
  const sky = () => userCam.current.view === "sky" && BOX.me.seat < 0;   // standing in the Sky Box: drag swings the camera round you
  const down = (e) => {
    if (e.button === 2 || e.button === 1 || (e.button === 0 && (front() || sky()))) drag = { x: e.clientX, y: e.clientY, moved: 0 };
  };
  const move = (e) => {
    if (!drag) return;
    const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
    drag = { x: e.clientX, y: e.clientY, moved: drag.moved + Math.abs(dx) + Math.abs(dy) };
    const uc = userCam.current;
    if (front()) {
      if (drag.moved < 4) return;                      // a click is not a look
      if (!uc.free) { uc.lookYaw = uc.frontYaw || 0; uc.lookPitch = uc.frontPitch || 0; uc.free = true; }   // start from where the head already is
      const sens = FRONT.sens * THREE.MathUtils.clamp((uc.camFov || FRONT.fovFree) / FRONT.fovFree, 0.2, 1);   // zoomed in = finer
      uc.lookYaw -= dx * sens;
      uc.lookPitch = THREE.MathUtils.clamp(uc.lookPitch - dy * sens, -1.25, 1.25);
      return;
    }
    if (sky()) {
      if (drag.moved < 4) return;
      if (uc.skyYaw == null) skyCamDefault(uc);
      uc.skyYaw -= dx * 0.006;
      uc.skyPitch = THREE.MathUtils.clamp(uc.skyPitch + dy * 0.004, -0.1, 1.2);
      return;
    }
    uc.yaw -= dx * 0.006;
    uc.pitch = THREE.MathUtils.clamp(uc.pitch + dy * 0.004, -0.25, 0.75);
  };
  const up = () => { drag = null; };
  const wheel = (e) => {
    const uc = userCam.current;
    if (front()) { uc.seatZoom = THREE.MathUtils.clamp((uc.seatZoom || 1) * (1 + Math.sign(e.deltaY) * 0.12), FRONT.zoom[0], FRONT.zoom[1]); return; }
    if (sky()) { if (uc.skyYaw == null) skyCamDefault(uc); uc.skyDist = THREE.MathUtils.clamp(uc.skyDist + Math.sign(e.deltaY) * 0.5, SKY.cam.min, SKY.cam.max); return; }
    uc.zoom = THREE.MathUtils.clamp(uc.zoom + Math.sign(e.deltaY) * 0.9, -5, 9);
  };
  const ctx = (e) => e.preventDefault();
  el.addEventListener("pointerdown", down);
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
  el.addEventListener("wheel", wheel, { passive: true });
  el.addEventListener("contextmenu", ctx);
  return () => {
    el.removeEventListener("pointerdown", down);
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
    el.removeEventListener("wheel", wheel);
    el.removeEventListener("contextmenu", ctx);
  };
}

// ── THE SKY BOX before the bell (Ringside): the same box camera, nobody on the plate yet ──
const NO_FIGHT = { sides: [{ active: -1, roster: [] }, { active: -1, roster: [] }], cinematic: null };
export function SkyOnlyCamera({ userCam, cue }) {
  const { camera, gl } = useThree();
  const S = useRef({ fov: 48, shake: [], t: 0 });
  useEffect(() => bindCameraInput(gl, userCam), [gl, userCam]);
  useFrame((state, dtRaw) => {
    const dt = Math.min(dtRaw || 0.016, 0.05);
    S.current.t += dt;
    skyFrame(state, dt, NO_FIGHT, userCam.current, S.current, camera, cue);
  });
  return null;
}

export default function BattleCamera({ battle, fx, userCam, mySide = 0, cue }) {
  const { camera, gl } = useThree();
  const S = useRef({ yaw: 0, dist: 12, pitch: PITCH, tx: 0, ty: TARGET_Y, tz: 0, fov: 48, shake: [], t: 0, swing: 1, init: false });

  // the shake & punch hooks the event pump calls
  useEffect(() => {
    fx.shake = (amp, dur) => S.current.shake.push({ amp, dur, t: dur });
    fx.punch = (amt = 4) => { S.current.fov -= amt; };
  }, [fx]);

  // drag to orbit, wheel to zoom — the pose is kept
  useEffect(() => bindCameraInput(gl, userCam), [gl, userCam]);

  useFrame((state, dtRaw) => {
    const b = battle.current; if (!b) return;
    const dt = Math.min(dtRaw, 0.05);
    const s = S.current;
    s.t += dt;
    const act = (side) => { const sd = b.sides[side]; return sd.active >= 0 ? sd.roster[sd.active] : null; };
    const me = act(mySide), foe = act(1 - mySide);   // the camera sits behind YOUR side (side 1 in an online PvP as guest)
    const uc = userCam.current;
    if (uc.view === "front" && uc.seat) { frontFrame(state, dt, b, uc, s, camera, cue); return; }
    if (uc.view === "sky") { skyFrame(state, dt, b, uc, s, camera, cue); return; }
    if (cue && cue.current && cue.current.hideOn) cue.current.hideOn = 0;   // back to the broadcast: your seat's fan returns

    let wantYaw = s.yaw, wantDist = s.dist, wantPitch = PITCH + uc.pitch, tx = s.tx, tz = s.tz, ty = TARGET_Y, rate = 2.2;
    const cine = b.cinematic;
    let star = null;
    if (cine) for (const sd of b.sides) for (const r of sd.roster) if (r.uid === cine.uid) star = r;

    if (cine && star && (cine.kind === "ult" || cine.kind === "transcend")) {
      // CINEMATIC: close, low, and slowly circling the being
      const lift = cine.kind === "transcend" ? 1.4 : 0.9;
      tx = star.x; tz = star.z; ty = lift;
      wantDist = cine.kind === "transcend" ? 5.2 : 4.6;
      wantPitch = cine.kind === "transcend" ? 0.12 : 0.08;
      wantYaw = star.yaw + Math.PI * 0.78 + s.t * 0.45;
      rate = 4.5;
    } else if (cine && star && cine.kind === "ruin") {
      tx = star.x; tz = star.z; ty = 0.7;
      wantDist = 6.5; wantPitch = 0.3;
      rate = 3;
    } else if (me || foe) {
      // BROADCAST: both in frame, weighted toward you
      const a = me || foe, c = foe || me;
      tx = a.x * 0.55 + c.x * 0.45; tz = a.z * 0.55 + c.z * 0.45;
      const d = Math.hypot(c.x - a.x, c.z - a.z);
      const axis = me && foe ? Math.atan2(foe.x - me.x, foe.z - me.z) : Math.PI;
      // keep swinging to whichever side we were already on, so the
      // camera never whips across the line of action
      wantYaw = axis + Math.PI - SIDE_SWING * s.swing + uc.yaw;
      wantDist = THREE.MathUtils.clamp(5.6 + d * 0.75, DIST_MIN, DIST_MAX);
    }
    wantDist += uc.zoom;
    if (!s.init) { s.yaw = wantYaw; s.dist = wantDist; s.tx = tx; s.tz = tz; s.init = true; }

    s.yaw = dampAngle(s.yaw, wantYaw, rate * 0.55, dt);
    s.dist = damp(s.dist, wantDist, rate, dt);
    s.pitch = damp(s.pitch, wantPitch, rate, dt);
    s.tx = damp(s.tx, tx, rate * 1.4, dt); s.tz = damp(s.tz, tz, rate * 1.4, dt); s.ty = damp(s.ty, ty, rate, dt);

    // shake: summed, decaying, cheap noise
    let sx = 0, sy = 0;
    for (const k of s.shake) {
      k.t -= dt;
      const a = k.amp * Math.max(0, k.t / k.dur) * 0.35;
      sx += Math.sin(s.t * 71 + k.dur * 13) * a; sy += Math.sin(s.t * 53 + k.amp * 7) * a;
    }
    for (let i = s.shake.length - 1; i >= 0; i--) if (s.shake[i].t <= 0) { s.shake[i] = s.shake[s.shake.length - 1]; s.shake.pop(); }

    const cp = Math.cos(s.pitch), sp = Math.sin(s.pitch);
    tgt.set(s.tx, s.ty, s.tz);
    v.set(s.tx + Math.sin(s.yaw) * s.dist * cp, s.ty + s.dist * sp + 0.6, s.tz + Math.cos(s.yaw) * s.dist * cp);
    camera.position.set(v.x + sx, v.y + sy, v.z);
    uc.camYaw = s.yaw;   // published for camera-relative steering
    camera.lookAt(tgt.x + sx * 0.3, tgt.y + sy * 0.3, tgt.z);

    // fov punch springs back
    s.fov = damp(s.fov, cine && cine.kind !== "ruin" ? 42 : 48, 3.5, dt);   // punches breathe back
    if (Math.abs(camera.fov - s.fov) > 0.01) { camera.fov = s.fov; camera.updateProjectionMatrix(); }
  });
  return null;
}
